// Texto a insertar: null, undefined y false no muestran nada; 0 sí se muestra
const toText = (v) => (v === null || v === undefined || v === false ? "" : v);

export class TemplateEngine {
  static render(template, data = {}) {
    let result = template;

    // Procesar loops anidados primero (de adentro hacia afuera)
    result = TemplateEngine.processNestedLoops(result, data);

    // Reemplazar variables simples {{variable}}
    // Los reemplazos usan una función: con un string, replace() interpreta los
    // patrones con $ dentro del valor (ej. "US$$ 100" quedaría "US$ 100").
    Object.keys(data).forEach((key) => {
      const regex = new RegExp(`{{${key}}}`, "g");
      result = result.replace(regex, () => toText(data[key]));
    });

    // Reemplazar variables anidadas {{object.property}}
    result = result.replace(/{{(\w+)\.(\w+)}}/g, (match, obj, prop) => {
      return data[obj] ? toText(data[obj][prop]) : "";
    });

    // Reemplazar arrays indexados {{array.0}}
    result = result.replace(/{{(\w+)\.(\d+)}}/g, (match, arrayName, index) => {
      return data[arrayName] ? toText(data[arrayName][index]) : "";
    });

    // Procesar condicionales mejorados
    result = TemplateEngine.processConditionals(result, data);

    // Limpiar variables no reemplazadas
    result = result.replace(/{{[^}]+}}/g, "");

    return result;
  }

  static processNestedLoops(template, data) {
    let result = template;
    let hasNestedLoops = true;
    let iterations = 0;
    const maxIterations = 10; // Prevenir loops infinitos

    while (hasNestedLoops && iterations < maxIterations) {
      hasNestedLoops = false;
      iterations++;

      // Buscar loops {{#each array}} más internos primero
      const loopRegex = /{{#each\s+(\w+)}}([\s\S]*?){{\/each}}/g;
      let match;

      while ((match = loopRegex.exec(result)) !== null) {
        const [fullMatch, arrayName, itemTemplate] = match;
        const array = data[arrayName] || [];

        // Verificar si este loop contiene otros loops
        const hasInnerLoop = /{{#each\s+\w+}}/.test(itemTemplate);

        if (!hasInnerLoop) {
          // Procesar este loop
          const processedContent = array
            .map((item, index) => {
              let itemResult = itemTemplate;

              // Si el item es un valor primitivo (string, number), reemplazar {{this}}
              if (typeof item === "string" || typeof item === "number") {
                itemResult = itemResult.replace(/{{this}}/g, () => item);
              } else if (typeof item === "object" && item !== null) {
                // Si es un objeto, reemplazar propiedades del item
                Object.keys(item).forEach((key) => {
                  const regex = new RegExp(`{{${key}}}`, "g");
                  itemResult = itemResult.replace(regex, () => toText(item[key]));
                });
              }

              // Reemplazar índice
              itemResult = itemResult.replace(/{{@index}}/g, index);

              // Procesar condicionales dentro del item
              itemResult = TemplateEngine.processConditionals(itemResult, item);

              return itemResult;
            })
            .join("");

          result = result.replace(fullMatch, () => processedContent);
          hasNestedLoops = true;
          break; // Reiniciar la búsqueda desde el principio
        }
      }
    }

    return result;
  }

  static processConditionals(template, data) {
    let result = template;

    // Procesar condicionales {{#if condition}} de forma recursiva
    const processIfStatements = (text, context) => {
      // Buscar condicionales anidados (más internos primero)
      let hasConditionals = true;
      let iterations = 0;
      const maxIterations = 10;

      while (hasConditionals && iterations < maxIterations) {
        hasConditionals = false;
        iterations++;

        let match;
        const newIfRegex = /{{#if\s+(\w+)}}([\s\S]*?){{\/if}}/g;

        while ((match = newIfRegex.exec(text)) !== null) {
          const [fullMatch, condition, content] = match;

          // Verificar si hay condicionales anidados en el contenido
          const hasNestedIf = /{{#if\s+\w+}}/.test(content);

          if (!hasNestedIf) {
            // Procesar este condicional
            const shouldShow = TemplateEngine.evaluateCondition(condition, context);
            const replacement = shouldShow ? content : "";
            text = text.replace(fullMatch, () => replacement);
            hasConditionals = true;
            break;
          }
        }
      }

      return text;
    };

    // Procesar condicionales negativas {{#unless condition}}
    const processUnlessStatements = (text, context) => {
      const unlessRegex = /{{#unless\s+(\w+)}}([\s\S]*?){{\/unless}}/g;
      return text.replace(unlessRegex, (match, condition, content) => {
        const shouldShow = !TemplateEngine.evaluateCondition(condition, context);
        return shouldShow ? content : "";
      });
    };

    result = processIfStatements(result, data);
    result = processUnlessStatements(result, data);

    return result;
  }

  static evaluateCondition(condition, context) {
    // Manejar condiciones simples
    if (Object.prototype.hasOwnProperty.call(context, condition)) {
      const value = context[condition];

      // Valores falsy
      if (value === false || value === null || value === undefined || value === "" || value === 0) {
        return false;
      }

      // Arrays vacíos
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }

      // Objetos vacíos
      if (typeof value === "object" && value !== null && Object.keys(value).length === 0) {
        return false;
      }

      return true;
    }

    // Manejar condiciones con punto (object.property)
    if (condition.includes(".")) {
      const parts = condition.split(".");
      let current = context;

      for (const part of parts) {
        if (current && Object.prototype.hasOwnProperty.call(current, part)) {
          current = current[part];
        } else {
          return false;
        }
      }

      return TemplateEngine.evaluateCondition("value", { value: current });
    }

    return false;
  }

  static helpers = {
    formatCurrency: (amount) => {
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
      }).format(amount);
    },

    formatDate: (date) => {
      return new Date(date).toLocaleDateString("es-AR");
    },

    truncate: (text, length = 50) => {
      return text.length > length ? text.substring(0, length) + "..." : text;
    },

    // Helper para generar estrellas
    generateStars: (rating) => {
      const fullStars = Math.floor(rating);
      const emptyStars = 5 - fullStars;
      return "★".repeat(fullStars) + "☆".repeat(emptyStars);
    },
  };
}
